import React, { useEffect, useState } from 'react';

import gql from 'graphql-tag';
import { useLazyQuery, useMutation, useQuery } from 'react-apollo';
import styled from 'styled-components';

import VenuePicker from 'Components/VenuePicker';
import { useCurrency, useI18n } from 'Hooks';

const CREATE_CASH_SOURCE = gql`
  mutation CreateCashSource($input: CreateCashSourceInput!) {
    createCashSource(input: $input) {
      success
      message
      error
    }
  }
`;

const DELETE_CASH_SOURCE = gql`
  mutation DeleteCashSource($sourceId: String!) {
    deleteCashSource(sourceId: $sourceId) {
      success
      message
      error
    }
  }
`;

const VENUE_QUERY = gql`
  query GetVenue($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      cashFloat
      cashSources {
        id
        name
      }
    }
  }
`;

const UPDATE_VENUE_METADATA = gql`
  mutation updateVenueMetadata($input: UpdateVenueMetadataInput!) {
    updateVenueMetadata(input: $input) {
      success
      error
      message
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
  width: 400px;
  padding: 30px;
`;

const FloatContainer = styled.div`
  display: flex;
  margin-bottom: 10px;
  padding-bottom: 18px;
  margin-top: 10px;
  width: 100%;
  border-bottom: 1px solid #ccc;
`;

const InputContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
  margin-top: 10px;
  width: 100%;
`;

const Input = styled.input`
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
  margin-right: 10px;
`;

const CashSourceContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const Button = styled.button`
  padding: 4px 8px;
  font-weight: bold;
  border-radius: 4px;
  background-color: #007bff;
  color: white;
  border: none;
  cursor: pointer;
`;

const DarkButton = styled(Button)`
  background-color: #555;
`;

const SubTitle = styled.p`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
`;

const Cash: React.FC = () => {
  const [newCashSource, setNewCashSource] = useState('');
  const [float, setFloat] = useState('');
  const [venueId, setVenueId] = useState('');

  const currency = useCurrency();

  const [createCashSource, { loading: createCashSourceLoading }] = useMutation(CREATE_CASH_SOURCE);
  const [deleteCashSource, { loading: deleteCashSourceLoading }] = useMutation(DELETE_CASH_SOURCE);

  const [updateVenueMetadata] = useMutation(UPDATE_VENUE_METADATA);

  const [getVenueData, { data: venueData, loading: venueDataLoading, refetch: refetchVenueData }] =
    useLazyQuery(VENUE_QUERY, {
      fetchPolicy: 'network-only',
    });

  useEffect(() => {
    if (venueId) {
      getVenueData({
        variables: {
          venueId,
        },
      });
    }
  }, [venueId]);

  const handleAddCashSource = async () => {
    try {
      await createCashSource({
        variables: {
          input: {
            venueId: venueId,
            name: newCashSource,
          },
        },
      });
      setNewCashSource('');
      refetchVenueData();
    } catch (error) {
      console.error('Error creating cash source:', error);
    }
  };

  const handleDeleteCashSource = async (id: string) => {
    deleteCashSource({ variables: { sourceId: id } });
  };

  const setFloatInVenueMetadata = () => {
    updateVenueMetadata({ variables: { input: { venueId, cashFloat: Number(float) * 100 } } })
      .then(() => {
        refetchVenueData();
        setFloat('');
      })
      .catch((error) => {
        console.error('Error updating venue metadata:', error);
      });
  };

  return (
    <Container>
      <VenuePicker onIDChange={(id) => setVenueId(id)} />
      <h1>Manage tenders</h1>
      <p>{`Current float: ${currency.formatAmount(venueData?.venue?.cashFloat)}`}</p>
      <FloatContainer>
        <Input type="number" value={float} onChange={(e) => setFloat(e.target.value)} />
        <Button onClick={() => setFloatInVenueMetadata()}>Update float</Button>
      </FloatContainer>
      <InputContainer>
        <Input
          type="text"
          value={newCashSource}
          onChange={(e) => setNewCashSource(e.target.value)}
        />
        <Button onClick={handleAddCashSource}>Add tender</Button>
      </InputContainer>
      <SubTitle>Added tenders</SubTitle>
      {venueData?.venue?.cashSources?.map((cashSource) => (
        <CashSourceContainer key={cashSource.id}>
          {cashSource.name}
          <DarkButton onClick={() => handleDeleteCashSource(cashSource.id)}>Delete</DarkButton>
        </CashSourceContainer>
      ))}
    </Container>
  );
};

export default Cash;
